import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getAudioUrl } from "@/lib/audio-utils";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Play,
  Pause,
  Heart,
  Trash2,
  Loader2,
  Sparkles,
  Swords,
  Target,
  Search,
  X,
} from "lucide-react";

const typeIcons: Record<string, any> = {
  competition: Swords,
  practice: Target,
  recovery: Heart,
};

interface SessionRatingState {
  viz: any;
  preRating: number;
  audioStartTime: number | null;
}

const LibraryPage = () => {
  const { user } = useAuth();
  const [visualizations, setVisualizations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Pre-rating dialog state
  const [preRatingViz, setPreRatingViz] = useState<any | null>(null);
  const [preRatingValue, setPreRatingValue] = useState(5);

  // Post-rating dialog state
  const [postRatingSession, setPostRatingSession] = useState<SessionRatingState | null>(null);
  const [postRatingValue, setPostRatingValue] = useState(5);
  const [savingSession, setSavingSession] = useState(false);

  const loadVisualizations = async () => {
    if (!user) return;
    let query = supabase
      .from("visualizations")
      .select("*")
      .eq("user_id", user.id)
      .eq("generation_status", "completed")
      .order("created_at", { ascending: false });

    if (filter !== "all") {
      query = query.eq("visualization_type", filter as any);
    }

    const { data } = await query;
    setVisualizations(data || []);
    setLoading(false);
  };

  useEffect(() => {
    loadVisualizations();
  }, [user, filter]);

  const startPlayback = async (viz: any, preRating: number) => {
    const url = await getAudioUrl(viz.audio_storage_path, viz.audio_url);
    if (audioRef.current && url) {
      audioRef.current.src = url;
      audioRef.current.play();
    }
    setPlayingId(viz.id);
    // Store session context for post-rating
    setPostRatingSession({ viz, preRating, audioStartTime: Date.now() });
  };

  const togglePlay = async (viz: any) => {
    if (playingId === viz.id) {
      audioRef.current?.pause();
      setPlayingId(null);
    } else {
      // Pause any currently playing audio first
      if (playingId) {
        audioRef.current?.pause();
        setPlayingId(null);
      }
      // Show pre-rating dialog
      setPreRatingValue(5);
      setPreRatingViz(viz);
    }
  };

  const handlePreRatingSubmit = async () => {
    if (!preRatingViz) return;
    const viz = preRatingViz;
    const rating = preRatingValue;
    setPreRatingViz(null);
    await startPlayback(viz, rating);
  };

  const handleAudioEnded = () => {
    setPlayingId(null);
    if (postRatingSession) {
      setPostRatingValue(5);
      // Dialog will open because postRatingSession is set
    }
  };

  const handlePostRatingSubmit = async () => {
    if (!postRatingSession || !user) return;
    setSavingSession(true);
    try {
      const { viz, preRating, audioStartTime } = postRatingSession;
      const durationSeconds = audioStartTime
        ? Math.round((Date.now() - audioStartTime) / 1000)
        : viz.audio_duration_seconds || null;
      const lift = postRatingValue - preRating;

      const { error } = await supabase.from("completed_sessions").insert({
        user_id: user.id,
        visualization_id: viz.id,
        duration_seconds: durationSeconds,
        pre_mental_performance: preRating,
        post_mental_performance: postRatingValue,
        mental_performance_lift: lift,
        completed_at: new Date().toISOString(),
      });

      if (error) throw error;

      toast.success(
        lift > 0
          ? `Great session! Mental performance +${lift} 🎯`
          : lift === 0
          ? "Session complete! Keep it up."
          : "Session recorded."
      );
    } catch (err) {
      console.error("Failed to save session:", err);
      toast.error("Session recorded but stats could not be saved.");
    } finally {
      setSavingSession(false);
      setPostRatingSession(null);
    }
  };

  const handlePostRatingSkip = () => {
    setPostRatingSession(null);
    toast.success("Session complete!");
  };

  const toggleFavorite = async (viz: any) => {
    const newVal = !viz.is_favorite;
    await supabase.from("visualizations").update({ is_favorite: newVal }).eq("id", viz.id);
    setVisualizations((prev) =>
      prev.map((v) => (v.id === viz.id ? { ...v, is_favorite: newVal } : v))
    );
  };

  const confirmDelete = async () => {
    if (!deletingId) return;
    const viz = visualizations.find((v) => v.id === deletingId);
    if (playingId === deletingId) {
      audioRef.current?.pause();
      setPlayingId(null);
      setPostRatingSession(null);
    }
    if (viz?.audio_storage_path) {
      await supabase.storage.from("visualization-audio").remove([viz.audio_storage_path]);
    }
    await supabase.from("visualizations").delete().eq("id", deletingId);
    setVisualizations((prev) => prev.filter((v) => v.id !== deletingId));
    setDeletingId(null);
    toast.success("Visualization deleted");
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return "--:--";
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const filtered = visualizations.filter((v) =>
    search ? v.title?.toLowerCase().includes(search.toLowerCase()) : true
  );

  const favorites = filtered.filter((v) => v.is_favorite);
  const rest = filtered.filter((v) => !v.is_favorite);

  return (
    <AppLayout>
      <audio
        ref={audioRef}
        onEnded={handleAudioEnded}
      />

      {/* Pre-session rating dialog */}
      <Dialog open={!!preRatingViz} onOpenChange={(open) => { if (!open) setPreRatingViz(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Before You Listen</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-6">
            <p className="text-sm text-muted-foreground">
              {preRatingViz?.visualization_type === "recovery"
                ? "Rate how relaxed you feel right now so we can measure your progress after the session."
                : "Rate your current mental readiness so we can measure your progress after the session."}
            </p>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {preRatingViz?.visualization_type === "recovery" ? "Relaxation" : "Mental Readiness"}
                </span>
                <span className="font-display text-2xl font-bold text-primary">{preRatingValue}</span>
              </div>
              <Slider
                min={1}
                max={10}
                step={1}
                value={[preRatingValue]}
                onValueChange={([v]) => setPreRatingValue(v)}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{preRatingViz?.visualization_type === "recovery" ? "1 — Tense" : "1 — Not Ready"}</span>
                <span>{preRatingViz?.visualization_type === "recovery" ? "10 — Fully Relaxed" : "10 — Peak Ready"}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="hero" className="w-full" onClick={handlePreRatingSubmit}>
              Start Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-session rating dialog */}
      <Dialog
        open={!!postRatingSession && !playingId}
        onOpenChange={(open) => { if (!open) handlePostRatingSkip(); }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>How Did That Feel?</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-6">
            <p className="text-sm text-muted-foreground">
              {postRatingSession?.viz.visualization_type === "recovery"
                ? "Rate how relaxed you feel now to track your progress."
                : "Rate your mental readiness now to track your lift."}
            </p>
            {postRatingSession && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/50">
                <span className="text-sm text-muted-foreground">Before session</span>
                <span className="font-semibold">{postRatingSession.preRating} / 10</span>
              </div>
            )}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">After session</span>
                <span className="font-display text-2xl font-bold text-primary">{postRatingValue}</span>
              </div>
              <Slider
                min={1}
                max={10}
                step={1}
                value={[postRatingValue]}
                onValueChange={([v]) => setPostRatingValue(v)}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>1 — Very Low</span>
                <span>10 — Peak</span>
              </div>
            </div>
            {postRatingSession && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-primary/10 border border-primary/30">
                <span className="text-sm font-medium">Performance Lift</span>
                <span className={`font-display text-lg font-bold ${
                  postRatingValue - postRatingSession.preRating > 0
                    ? "text-green-400"
                    : postRatingValue - postRatingSession.preRating < 0
                    ? "text-red-400"
                    : "text-muted-foreground"
                }`}>
                  {postRatingValue - postRatingSession.preRating > 0 ? "+" : ""}
                  {postRatingValue - postRatingSession.preRating}
                </span>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handlePostRatingSkip} disabled={savingSession}>
              Skip
            </Button>
            <Button variant="hero" onClick={handlePostRatingSubmit} disabled={savingSession}>
              {savingSession ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Session
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Delete confirmation dialog */}
      <Dialog open={!!deletingId} onOpenChange={(open) => { if (!open) setDeletingId(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Visualization?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will permanently delete this session and its audio. This cannot be undone.
          </p>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDeletingId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold mb-2">Your Library</h1>
        <p className="text-muted-foreground">All your visualization sessions in one place.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search visualizations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full h-10 pl-10 pr-4 rounded-xl border border-border/50 bg-secondary/30 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring text-sm"
          />
          {search && (
            <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2">
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 -mb-1">
          {["all", "competition", "practice", "recovery"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm capitalize transition-colors ${
                filter === f
                  ? "bg-primary/10 text-primary font-medium border border-primary/30"
                  : "bg-secondary/30 text-muted-foreground border border-border/50 hover:border-primary/20"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20">
          <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-display text-xl font-semibold mb-2">No visualizations yet</h3>
          <p className="text-muted-foreground mb-6">Create your first session to see it here.</p>
          <Button variant="hero" onClick={() => window.location.href = "/create/competition"}>
            Start a Session
          </Button>
        </div>
      ) : (
        <div className="space-y-8">
          {favorites.length > 0 && (
            <div>
              <h2 className="font-display text-lg font-semibold mb-4 flex items-center gap-2">
                <Heart className="w-4 h-4 text-primary fill-primary" /> Favorites
              </h2>
              <div className="grid gap-3">
                {favorites.map((viz) => (
                  <VizCard
                    key={viz.id}
                    viz={viz}
                    isPlaying={playingId === viz.id}
                    onPlay={() => togglePlay(viz)}
                    onFavorite={() => toggleFavorite(viz)}
                    onDelete={() => setDeletingId(viz.id)}
                    formatDuration={formatDuration}
                  />
                ))}
              </div>
            </div>
          )}

          <div>
            {favorites.length > 0 && rest.length > 0 && (
              <h2 className="font-display text-lg font-semibold mb-4">All Sessions</h2>
            )}
            <div className="grid gap-3">
              {rest.map((viz) => (
                <VizCard
                  key={viz.id}
                  viz={viz}
                  isPlaying={playingId === viz.id}
                  onPlay={() => togglePlay(viz)}
                  onFavorite={() => toggleFavorite(viz)}
                  onDelete={() => setDeletingId(viz.id)}
                  formatDuration={formatDuration}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

const VizCard = ({
  viz,
  isPlaying,
  onPlay,
  onFavorite,
  onDelete,
  formatDuration,
}: {
  viz: any;
  isPlaying: boolean;
  onPlay: () => void;
  onFavorite: () => void;
  onDelete: () => void;
  formatDuration: (s: number | null) => string;
}) => {
  const Icon = typeIcons[viz.visualization_type] || Sparkles;
  return (
    <div className="gradient-card rounded-xl border border-border/50 shadow-card p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
      {/* Play button */}
      <button
        onClick={onPlay}
        className="w-12 h-12 rounded-full gradient-cta flex items-center justify-center flex-shrink-0 hover:scale-105 transition-transform"
      >
        {isPlaying ? (
          <Pause className="w-5 h-5 text-primary-foreground" />
        ) : (
          <Play className="w-5 h-5 text-primary-foreground ml-0.5" />
        )}
      </button>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <h3 className="font-display text-sm sm:font-semibold truncate">{viz.title || "Untitled Session"}</h3>
        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
          <span className="flex items-center gap-1 capitalize">
            <Icon className="w-3 h-3" />
            {viz.visualization_type}
          </span>
          <span>{formatDuration(viz.audio_duration_seconds)}</span>
          <span>{new Date(viz.created_at).toLocaleDateString()}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1">
        <button onClick={onFavorite} className="p-2 rounded-lg hover:bg-secondary/50 transition-colors">
          <Heart className={`w-4 h-4 ${viz.is_favorite ? "fill-primary text-primary" : "text-muted-foreground"}`} />
        </button>
        <button onClick={onDelete} className="p-2 rounded-lg hover:bg-destructive/10 transition-colors">
          <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
        </button>
      </div>
    </div>
  );
};

export default LibraryPage;
